/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { ReactElement } from 'react';

import classNames from 'classnames';
import { Empty } from '@douyinfe/semi-ui';
import {
  IllustrationNoContent,
  IllustrationNoContentDark,
  IllustrationNoResult,
  IllustrationNoResultDark,
} from '@douyinfe/semi-illustrations';
import { resolveAbrEmptyIcon } from '@coze-arch/bot-icons';

import { Button } from '../../components/ui-button';

import s from './index.module.less';

export interface EmptyProps {
  title?: string;
  icon?: ReactElement;
  iconDarkMode?: ReactElement;
  description?: string;
  btnText?: string;
  loading?: boolean;
  btnOnClick?: () => void;
}

export interface UIEmptyProps {
  className?: string;
  isNotFound?: boolean;
  empty?: EmptyProps;
  notFound?: EmptyProps;
}

enum EmptyButtonOpacity {
  Disable = 0.6,
  UnDisable = 1,
}

const DEFAULT_EMPTY_ILLUSTRATION = (
  <IllustrationNoContent style={{ width: 150, height: '100%' }} />
);
const DEFAULT_EMPTY_ILLUSTRATION_DARK = (
  <IllustrationNoContentDark style={{ width: 150, height: '100%' }} />
);
const DEFAULT_NOT_FOUND_ILLUSTRATION = (
  <IllustrationNoResult style={{ width: 150, height: '100%' }} />
);
const DEFAULT_NOT_FOUND_ILLUSTRATION_DARK = (
  <IllustrationNoResultDark style={{ width: 150, height: '100%' }} />
);
const ABR_EMPTY_ICON = resolveAbrEmptyIcon(DEFAULT_EMPTY_ILLUSTRATION, 'empty');
const ABR_NOT_FOUND_ICON = resolveAbrEmptyIcon(
  DEFAULT_NOT_FOUND_ILLUSTRATION,
  'empty',
);

function resolveDefaultEmptyImage(icon?: ReactElement) {
  return icon ?? ABR_EMPTY_ICON;
}

function resolveDefaultEmptyDarkImage(
  icon?: ReactElement,
  iconDarkMode?: ReactElement,
) {
  if (iconDarkMode) {
    return iconDarkMode;
  }
  if (icon) {
    return DEFAULT_EMPTY_ILLUSTRATION_DARK;
  }
  return ABR_EMPTY_ICON;
}

function resolveDefaultNotFoundImage(icon?: ReactElement) {
  return icon ?? ABR_NOT_FOUND_ICON;
}

function resolveDefaultNotFoundDarkImage(
  icon?: ReactElement,
  iconDarkMode?: ReactElement,
) {
  if (iconDarkMode) {
    return iconDarkMode;
  }
  if (icon) {
    return DEFAULT_NOT_FOUND_ILLUSTRATION_DARK;
  }
  return ABR_NOT_FOUND_ICON;
}

export function UIEmpty({
  className,
  isNotFound = false,
  empty,
  notFound,
}: UIEmptyProps) {
  return (
    <div className={classNames(s['ui-empty'], className)}>
      {isNotFound ? (
        <Empty
          title={notFound?.title}
          image={resolveDefaultNotFoundImage(notFound?.icon)}
          darkModeImage={resolveDefaultNotFoundDarkImage(
            notFound?.icon,
            notFound?.iconDarkMode,
          )}
        ></Empty>
      ) : (
        <Empty
          title={empty?.title}
          description={empty?.description || ''}
          image={resolveDefaultEmptyImage(empty?.icon)}
          darkModeImage={resolveDefaultEmptyDarkImage(
            empty?.icon,
            empty?.iconDarkMode,
          )}
        >
          {!!empty?.btnText && (
            <Button
              theme="solid"
              onClick={empty?.btnOnClick}
              loading={empty?.loading}
              style={{
                opacity: empty?.loading
                  ? EmptyButtonOpacity.Disable
                  : EmptyButtonOpacity.UnDisable,
              }}
            >
              {empty.btnText}
            </Button>
          )}
        </Empty>
      )}
    </div>
  );
}

// Native usage in non-graphic scenarios
UIEmpty.Semi = Empty;

export default UIEmpty;
